<template>
  <div class="pages">
    <h1 v-bg-typography="'h1'">
      Pages
    </h1>

    <div class="filters">
      <bg-field class="field" label="Info">
        <bg-select
          @change="selectInfo"
          :options="infoOptions"
          :value="activeTab"
        ></bg-select>
      </bg-field>

      <bg-field
        style="max-width: 250px"
        label="Page"
        v-if="activeTab === 'Feedback'"
      >
        <bg-select
          @change="selectPage"
          :options="data"
          label="name"
          :value="activePage"
        ></bg-select>
      </bg-field>

      <bg-field :style="`max-width: ${getWidth()}px;`" label="Data">
        <div class="data" style="display: flex; gap: 5px">
          <div
            class="datacard"
            v-bind:class="
              activeEmotion === 'frustration' ? 'active-button' : ''
            "
            @click="activateEmotion('frustration')"
          >
            <p>Frustration</p>
          </div>
          <div
            class="datacard"
            v-bind:class="activeEmotion === 'joy' ? 'active-button' : ''"
            @click="activateEmotion('joy')"
          >
            <p>Joy</p>
          </div>
          <div
            class="datacard"
            v-bind:class="activeEmotion === 'attention' ? 'active-button' : ''"
            @click="activateEmotion('attention')"
          >
            <p>Attention</p>
          </div>
          <div
            v-if="this.activeTab === 'Compare'"
            v-bind:class="activeComparison === 'Time' ? 'active-button' : ''"
            @click="selectTime"
            class="datacard"
          >
            <p>Time</p>
          </div>
          <div
            v-if="this.activeTab === 'Compare'"
            v-bind:class="activeComparison === 'Visits' ? 'active-button' : ''"
            @click="selectVisits"
            class="datacard"
          >
            <p>Visits</p>
          </div>
        </div>
      </bg-field>
    </div>

    <div class="nodata">
      <h3
        v-if="this.top3.length === 0 && activeTab != 'Feedback' && !isLoading"
        v-bg-typography="'h3'"
      >
        No data to be shown. Adjust the filters first or add pages to
        participants.
      </h3>
    </div>
    <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    <div
      v-if="!isLoading && activeTab === 'Compare' && this.top3.length > 0"
      class="sub"
    >
      <div class="small" v-if="!isLoading">
        <bar-chart :options="options" :chartData="datacollection"></bar-chart>
        <p
          v-if="
            this.top3[0] &&
              this.activeComparison != 'Time' &&
              this.activeComparison != 'Visits'
          "
          @click="activatePage()"
          style="margin-top: 20px; text-align: end; cursor: pointer"
        >
          <u
            >Find out why {{ this.top3[0].label }} scores relatively high on
            {{ this.activeEmotion }} &rarr;</u
          >
        </p>
      </div>
    </div>
    <div
      v-if="!isLoading && activeTab === 'Ranking' && this.top3.length > 0"
      class="sub"
    >
      <h3 v-bg-typography="'h3'">Top 3 pages with most {{ activeEmotion }}</h3>
      <div class="top-three">
        <div
          class="card"
          v-for="(page, index) in this.top3"
          :key="index"
          v-bind:class="activeRankIndex === index ? 'active-card' : ''"
          @click="activeRankIndex = index"
        >
          <h2>
            <b>#{{ `${index + 1}. ` }}</b> {{ page.label }}
          </h2>
          <p style="margin-bottom: 15px">
            Visited by <b>{{ page.participants }}</b> participants
          </p>
          <div
            class="bar"
            :style="
              `background: linear-gradient(${
                barWidth(page).average.degree
              }deg, ${barWidth(page).average.first} ${
                barWidth(page).average.average
              }%, ${barWidth(page).average.second} ${100 -
                barWidth(page).average.average}%);`
            "
          >
            <p>
              {{ page.emotion_score - 100 }}%
              {{ barWidth(page).direction }} than average
            </p>
          </div>
          <div
            class="bar"
            :style="
              `background: linear-gradient(${barWidth(page).peaks.degree}deg, ${
                barWidth(page).peaks.first
              } ${barWidth(page).peaks.peaks}%, ${
                barWidth(page).peaks.second
              } ${100 - barWidth(page).peaks.peaks}%);`
            "
          >
            <p>
              <b>{{ page.peaks.length }}</b
              >x {{ activeEmotion }} feedbacks
            </p>
          </div>
        </div>
      </div>
      <h3 style="margin-top: 25px" v-bg-typography="'h3'">Why</h3>
      <div v-if="this.top3.length > 0">
        <bg-detail-card
          v-for="feedback in top3[this.activeRankIndex].peaks"
          v-bind:key="feedback.id"
          v-bind="{ ...feedback }"
          :text="feedback.comment"
          :detail="feedback.emotion"
          @click.native="selectFeedback(feedback)"
          actionIcon="videos"
          actionText="Watch recording"
          :actionTextTarget="`Participant ${feedback.participant_id}`"
        />
      </div>
    </div>
    <div v-if="!isLoading && activeTab === 'Feedback'" class="sub">
      <div class="select-top">
        <p><b>Participant feedback</b></p>
        <br />
      </div>
      <div v-for="feedback in feedbacks" :key="feedback.id">
        <bg-detail-card
          :text="feedback.comment"
          :detail="feedback.emotion"
          @click.native="selectFeedback(feedback)"
          actionIcon="videos"
          :insights="feedback.insights"
          actionText="Watch recording"
          :actionTextTarget="`Participant ${feedback.participant_id}`"
        />
        <p v-if="feedbacks.length === 0">No feedback found on this page!</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";

.small {
  max-width: 1000px;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BarChart from "./BarChart";
import API from "@/api";
import BgLoader from "@/components/bg-loader";
import Vue from "vue";
import BgField from "@/components/bg-field";
import BgSelect from "@/components/bg-select";
import BgInput from "@/components/bg-input";
import BgDetailCard from "@/components/bg-detail-card";
import BgIcon from "@/components/bg-icon";
import BgButton from "@/components/bg-button";

export default {
  components: {
    BgButton,
    BarChart,
    BgLoader,
    BgSelect,
    BgField,
    BgInput,
    BgDetailCard,
    BgIcon,
  },
  directives: {
    BgTypography,
  },
  watch: {
    minAmountOfTime: function() {
      this.filterData();
      this.generateNewDataset();
    },
    activeTab: function() {
      this.filterData();
      this.generateNewDataset();
    },
  },
  data() {
    return {
      activeComparison: "Emotions",
      activeEmotion: "frustration",
      activePage: "",
      activeTab: "Ranking",
      activeRankIndex: 0,
      allFeedback: [],
      data: null,
      datacollection: {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: "#00DEAB",
          },
        ],
      },
      emotionOptions: ["joy", "attention", "frustration"],
      insights: [],
      isLoading: false,
      infoOptions: ["Ranking", "Compare", "Feedback"],
      labels: [],
      minAmountOfPp: 5,
      minAmountOfTime: 0,
      optionsOfPp: [10, 9, 8, 7, 6, 5, 4, 3, 2, 1],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                suggestedMin: 0,
              },
              scaleLabel: {
                display: true,
                labelString: "Indexed",
              },
            },
          ],
        },
      },
      pageOptions: [],
      times: [],
      visits: [],
      visitsLabels: [],
      zScoresJoy: [],
      zScoresAttention: [],
      zScoresFrustration: [],
    };
  },
  computed: {
    feedbacks: function() {
      let filtered = [];

      this.data.forEach((label) => {
        let data = [];
        label.significant_points.forEach((el) => {
          if (el.comment) {
            const insights = [];
            this.insights.forEach((insight) => {
              const ind = insight.significant_points_ids.findIndex(
                (o) => o === el.id
              );
              if (ind > -1) {
                insights.push(insight.title);
              }
            });
            el.page = label.name;
            el.insights = insights;
            data.push(el);
          }
        });
        data.forEach((point) => filtered.push(point));
      });
      filtered = filtered.filter((el) => {
        return el.emotion === this.activeEmotion;
      });
      filtered = filtered.filter((el) => {
        return el.page === this.activePage.name;
      });
      return filtered;
    },
    top3: function() {
      const data = [];
      const emo = this.activeEmotion;

      if (this.labels.length > 0) {
        this.labels.forEach((el, index) => {
          let emoScore = 0;
          let sps = [];
          const peaks = this.data.find((label) => label.name === el)
            .significant_points;
          const participants = this.data.find((label) => label.name === el)
            .total_participants;

          if (emo === "frustration") {
            emoScore = this.zScoresFrustration[index];
            if (peaks) {
              peaks.forEach((el) => {
                if (!el.comment) {
                } else if (el.emotion === "frustration") {
                  sps.push(el);
                }
              });
            }
          } else if (emo === "attention") {
            emoScore = this.zScoresAttention[index];
            if (peaks) {
              peaks.forEach((el) => {
                if (el.emotion === "attention") {
                  sps.push(el);
                }
              });
            }
          } else if (emo === "joy") {
            emoScore = this.zScoresJoy[index];
            if (peaks) {
              peaks.forEach((el) => {
                if (el.emotion === "joy") {
                  sps.push(el);
                }
              });
            }
          }

          data.push({
            label: el,
            emotion_score: emoScore,
            peaks: sps,
            participants: participants,
          });
        });
      }

      data.sort(function(a, b) {
        return b.emotion_score - a.emotion_score;
      });

      const topthree = [];
      let i;
      let max;
      if (data.length < 4) {
        max = data.length;
      } else {
        max = 3;
      }
      for (i = 0; i < max; i++) {
        topthree.push(data[i]);
      }
      return topthree;
    },
  },
  created() {
    this.fetchData();
    if (this.$route.query.page) {
      this.activeRankIndex = Number(this.$route.query.page);
    }
  },
  methods: {
    activateEmotion(val) {
      this.activeComparison = "Emotions";
      this.activeEmotion = val;
      this.generateNewDataset();
    },
    activatePage() {
      const emo = this.activeEmotion;
      this.activeTab = "Ranking";
      this.activeRankIndex = 0;
      this.activeEmotion = emo;
    },
    barWidth(val) {
      let score = {
        direction: "",
        average: {
          average: 0,
          first: "#FF14A9",
          second: "transparent",
          degree: 90,
        },
        peaks: {
          peaks: 0,
          first: "#FF14A9",
          second: "transparent",
          degree: 90,
        },
      };

      if (this.activeEmotion === "frustration") {
        score.average.first = "#FF14A9";
        score.peaks.first = "#FF14A9";
      } else if (this.activeEmotion === "attention") {
        score.average.first = "#F1C40F";
        score.peaks.first = "#F1C40F";
      } else {
        score.average.first = "#00DEAB";
        score.peaks.first = "#00DEAB";
      }

      score.average.average = (val.emotion_score - 100) * 6;

      if (score.average.average < 50) {
        score.average.average = 100 - score.average.average;
        score.average.degree = 270;
        const first = score.average.first;
        score.average.first = score.average.second;
        score.average.second = first;
      }

      if (val.emotion_score > 100) {
        score.direction = "higher";
      } else {
        score.direction = "lower";
      }

      score.peaks.peaks = val.peaks.length * 10;

      if (score.peaks.peaks < 50) {
        score.peaks.peaks = 100 - score.peaks.peaks;
        score.peaks.degree = 270;
        const first = score.peaks.first;
        score.peaks.first = score.peaks.second;
        score.peaks.second = first;
      }

      return score;
    },
    async fetchData() {
      this.isLoading = true;
      const res = await API.test.getLabels(this.$route.params.id);
      this.data = res.data.labels;
      this.insights = [];
      const insights = await API.test.getInsights(this.$route.params.id);
      insights.data.insights.forEach((el) => {
        this.insights.push(el);
      });

      this.filterData();
      this.isLoading = false;
    },
    filterData() {
      this.isLoading = true;
      let joy = [];
      let attention = [];
      let frustration = [];
      this.labels = [];
      this.times = [];
      this.visits = [];
      this.visitsLabels = [];

      this.data.forEach((label) => {
        if (
          label.total_participants >= this.minAmountOfPp &&
          label.average_time >= this.minAmountOfTime * 1000
        ) {
          this.labels.push(label.name);
          joy.push(label.joy);
          attention.push(label.attention);
          frustration.push(label.frustration);
          this.times.push(Math.floor(label.average_time / 1000));
          label.significant_points.forEach((el) => {
            el.page = label.name;
            this.allFeedback.push(el);
          });
          if (label.total_participants > 0) {
            this.visits.push(label.total_participants);
            this.visitsLabels.push(label.name);
          }
        }
      });

      function getMean(data) {
        return (
          data.reduce(function(a, b) {
            return Number(a) + Number(b);
          }) / data.length
        );
      }

      function getSD(data) {
        let m = getMean(data);
        return Math.sqrt(
          data.reduce(function(sq, n) {
            return sq + Math.pow(n - m, 2);
          }, 0) /
            (data.length - 1)
        );
      }

      // Use these (instead of the ones below here) if we want to display z-scores instead of indexes
      // this.zScoresJoy = []
      // joy.forEach((el) => {
      //     const z = ((el - getMean(joy))/ getSD(joy))
      //     this.zScoresJoy.push(z)
      // })
      // this.zScoresAttention = []
      // attention.forEach((el) => {
      //     const z = ((el - getMean(attention))/ getSD(attention))
      //     this.zScoresAttention.push(z)
      // })
      // this.zScoresFrustration = []
      // frustration.forEach((el) => {
      //     const z = ((el - getMean(frustration))/ getSD(frustration))
      //     this.zScoresFrustration.push(z)
      // })

      this.zScoresJoy = [];
      joy.forEach((el) => {
        const z = (el / getMean(joy)) * 100;
        this.zScoresJoy.push(Math.floor(z));
      });
      this.zScoresAttention = [];
      attention.forEach((el) => {
        const z = (el / getMean(attention)) * 100;
        this.zScoresAttention.push(Math.floor(z));
      });
      this.zScoresFrustration = [];
      frustration.forEach((el) => {
        const z = (el / getMean(frustration)) * 100;
        this.zScoresFrustration.push(Math.floor(z));
      });

      this.generateNewDataset();
      this.isLoading = false;
    },
    generateNewDataset() {
      this.isLoading = true;
      this.datacollection = {};
      const newDataset = {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: "#00DEAB",
          },
        ],
      };
      const newOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                suggestedMin: 0,
              },
              scaleLabel: {
                display: true,
                labelString: "Indexed",
              },
            },
          ],
        },
      };
      if (this.activeComparison === "Emotions") {
        newOptions.scales.yAxes[0].scaleLabel.labelString = "Indexed";
        if (this.activeEmotion === "attention") {
          newDataset.datasets[0].data = this.zScoresAttention;
          newDataset.labels = this.labels;
          newDataset.datasets[0].backgroundColor = "#F1C40F";
        } else if (this.activeEmotion === "frustration") {
          newDataset.datasets[0].data = this.zScoresFrustration;
          newDataset.labels = this.labels;
          newDataset.datasets[0].backgroundColor = "#FF14A9";
        } else {
          newDataset.datasets[0].data = this.zScoresJoy;
          newDataset.labels = this.labels;
          newDataset.datasets[0].backgroundColor = "#00DEAB";
        }
      } else if (this.activeComparison === "Time") {
        newOptions.scales.yAxes[0].scaleLabel.labelString =
          "Average time in seconds";
        newDataset.datasets[0].data = this.times;
        newDataset.labels = this.labels;
        newDataset.datasets[0].backgroundColor = "#141A77";
      } else if (this.activeComparison === "Visits") {
        newOptions.scales.yAxes[0].scaleLabel.labelString =
          "Visited by # of participants";
        newDataset.datasets[0].data = this.visits;
        newDataset.labels = this.visitsLabels;
        newDataset.datasets[0].backgroundColor = "#636383";
      }

      this.options = newOptions;
      this.datacollection = newDataset;
      this.isLoading = false;
    },
    getWidth() {
      if (this.activeTab === "Compare") {
        return 347;
      } else {
        return 260;
      }
    },
    selectCompare(val) {
      this.activeComparison = val;
      this.generateNewDataset();
    },
    selectInfo(val) {
      if (!this.noData) {
        this.activeTab = val;
        if (val != "Compare") {
          if (
            this.activeEmotion != "joy" &&
            this.activeEmotion != "attention" &&
            this.activeEmotion != "frustration"
          ) {
            this.activeEmotion = "frustration";
            this.activeComparison = "Emotions";
          }
        }
        if (val === "Feedback") {
          this.activePage = this.data[0];
        }
      }
    },
    selectFeedback(val) {
      const routeObj = {
        path: `/test/${this.$route.params.id}/participant/${
          val.participant_id
        }/recording`,
        query: {
          timestamp: val.timestamp_top,
          sp_id: val.id,
        },
      };
      let routeData = this.$router.resolve(routeObj);
      window.open(routeData.href, "_blank");
    },
    selectPage(val) {
      this.activePage = val;
    },
    selectTime() {
      this.activeComparison = "Time";
      this.activeEmotion = "Time";
      this.generateNewDataset();
    },
    selectVisits() {
      this.activeComparison = "Visits";
      this.activeEmotion = "Visits";
      this.generateNewDataset();
    },
    selectMinPp(val) {
      this.minAmountOfPp = val;
      this.generateNewDataset();
    },
  },
};
</script>
